import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import FilterIcon from '../../assets/icons/filter-icon.svg?react';
import { toggleSwitch } from '../../reducers/switch';
import SearchInput from '../../components/SearchInput';
import FilterClearIcon from '../../assets/icons/filter-clear-icon.svg?react';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import Select from '../../components/Select';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import DownloadIcon from '../../assets/icons/download-icon.svg?react';

import { Link, useNavigate } from 'react-router';
import {
  Incidents,
  IncidentsSeverityEnum,
  IncidentsStatusEnum,
  IncidentReportingApiIncidentReportingControllerGetUserIncidentsRequest,
  GetUserIncidentsResponseOrganization,
} from '@EHSNavigator/backend';
import NoItemsFound from '../../components/NoItemsFound';
import moment from 'moment';
import Pagination from '../../components/CustomPagination';
import { useDebounce } from '@uidotdev/usehooks';
import Loader from '../../components/Loader';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import { useClickOutside } from '../../utils/clickOutSide';
import { handleExportToExcel } from '../../utils/exportTable';
import { handleExportToCSV } from '../../utils/exportTable';

interface IncidentReviewProps {
  otherClasses?: string;
  data: GetUserIncidentsResponseOrganization;
  departments: {
    label: string;
    value: string;
  }[];
  incidentTypes: {
    label: string;
    value: string;
  }[];
  noDataMessage: string;
  isIncidentsLoading: boolean;
  params: IncidentReportingApiIncidentReportingControllerGetUserIncidentsRequest;
  allDraftIncidentReviewsLength: number | undefined;
}

const IncidentReview: React.FC<IncidentReviewProps> = ({
  otherClasses,
  data,
  allDraftIncidentReviewsLength,
  departments,
  incidentTypes,
  noDataMessage,
  params,
  isIncidentsLoading,
}) => {
  const incidentReviewClasses = clsx(
    otherClasses,
    'flex flex-col w-full flex-1',
  );
  const [toggle, setToggle] = useState(false);
  const ref = useRef(null);
  useClickOutside({ ref, setToggle });
  const [state, setState] = useState({
    page: params?.page || 1,
    search: params?.search || '',
    status: params?.status || '',
    types: params?.incidentTypeId || '',
    severity: params?.severity || '',
    department: params?.departmentId || '',
    limit: params?.limit || 10,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const debouncedSearch = useDebounce(state?.search, 600);
  const prevFiltersRef = useRef({});
  const isFirstRun = useRef(true);

  useEffect(() => {
    const filters = {
      search: debouncedSearch,
      status: state.status,
      type: state.types,
      severity: state.severity,
      department: state.department,
      limit: state.limit,
    };
    let shouldResetPage = false;
    if (!isFirstRun.current) {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
      if (filtersChanged) {
        shouldResetPage = true;
        setState((prev) => ({ ...prev, page: 1 }));
      }
    }
    const paramsSearch = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'limit' && Number(value) === 10) {
        return;
      }
      if (value) paramsSearch.set(key, value.toString());
    });
    if (state.page !== 1) {
      paramsSearch.set('page', state.page.toString());
    }
    if (shouldResetPage) {
      paramsSearch.delete('page');
      setState((prev) => ({ ...prev, page: 1 }));
    }
    prevFiltersRef.current = filters;
    if (!isFirstRun.current) {
      navigate(`/incident-review?${paramsSearch.toString()}`);
    }
    isFirstRun.current = false;
  }, [
    debouncedSearch,
    state.status,
    state.types,
    state.severity,
    state.department,
    state.page,
    state.limit,
  ]);

  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);
    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }
    navigate(`/incident-review?${params.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };
  const lg = useButtonSizeLarge();

  const exportData = {
    header: [
      'Sr.',
      'Ref#',
      'Incident/Hazard',
      'Date of Report',
      'Type',
      'Severity',
      'Status',
    ],
    columnWidths: [
      { width: 5 },
      { width: 18 },
      { width: 30 },
      { width: 20 },
      { width: 30 },
      { width: 15 },
      { width: 15 },
    ],
    exportData: data?.incidents?.map((incident: any, index: number) => {
      return {
        'Sr.': (state.page - 1) * state.limit + index + 1,
        'Ref#': incident.incidentRefId,
        'Incident/Hazard': incident.title
          ? incident.title
              .split(' ')
              .map(
                (word: string) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
              )
              .join(' ')
          : '',
        'Date of Report': moment(incident.reportedAt).format('YYYY-M-D'),
        Type: incident.incidentType?.name || '',
        Severity: incident.severity,
        Status: incident.status,
      };
    }),
  };

  const options = [
    {
      label: 'Export CSV',
      onClick: () => {
        handleExportToCSV(exportData?.exportData, exportData?.header);
      },
    },
    {
      label: 'Export XLSX',
      onClick: () => {
        handleExportToExcel(
          exportData?.exportData,
          exportData?.header,
          exportData?.columnWidths,
          'incident-review',
        );
      },
    },
  ];

  return (
    <div className={incidentReviewClasses} data-testid="incident-review">
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="flex w-full items-center justify-between gap-6">
          <div>
            <h1 className="h3">Incident Review</h1>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              Review all incidents from this list and assign corrective actions.
            </p>
            {data?.reOpen !== 0 && (
              <p className="font-inter-variable text-brand-posh-pink mt-15 hidden items-center gap-1 text-xl leading-6 font-bold lg:flex">
                <WarningIcon /> {data?.reOpen} Reopened Incident(s)
              </p>
            )}
          </div>
          <Button
            variant="secondary"
            size={lg}
            href="/incident-review/drafts"
            otherClasses="relative whitespace-nowrap lg:flex  hidden"
          >
            <EditIcon className="h-6 w-6 [&>path]:fill-current" /> All Drafts{' '}
            {allDraftIncidentReviewsLength && (
              <span className="text-brand-posh-pink">
                {`(${allDraftIncidentReviewsLength})`}
              </span>
            )}
          </Button>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
        Review all incidents from this list and assign corrective actions.
      </p>
      {data?.reOpen !== 0 && (
        <p className="font-inter-variable text-brand-posh-pink mt-15 flex items-center gap-1 text-lg leading-5 font-bold lg:hidden">
          <WarningIcon /> {data?.reOpen} Reopened Incident(s)
        </p>
      )}
      <Button
        variant="secondary"
        size={lg}
        href="/incident-review/drafts"
        otherClasses="relative whitespace-nowrap lg:hidden mt-2.5 sm:w-fit"
      >
        <EditIcon className="h-6 w-6 [&>path]:fill-current" /> All Drafts{' '}
        {allDraftIncidentReviewsLength && (
          <span className="text-brand-posh-pink">
            {`(${allDraftIncidentReviewsLength})`}
          </span>
        )}
      </Button>
      <hr className="border-neutral-tones-text/50 mt-9 mb-6 lg:hidden" />
      <div className="flex items-stretch gap-2.5 lg:mt-12">
        <SearchInput
          name="search"
          onChange={(e) =>
            setState((prev) => ({
              ...prev,
              search: e?.target?.value,
            }))
          }
          placeholder="Enter title, ID, or department..."
          value={state?.search}
          disabled={false}
          errorMessage=""
          otherClasses={{ container: 'w-full' }}
        />
        <div ref={ref} className="relative flex lg:block">
          <Button
            onClick={() => setToggle(!toggle)}
            otherClasses="!py-0 flex-1 h-full"
            size="smaller"
            disable={data?.incidents?.length === 0}
          >
            <DownloadIcon className="rotate-180 [&>g>path]:fill-current" />{' '}
            <span className="hidden md:block">Export</span>
          </Button>
          {toggle && (
            <ul
              className={clsx(
                'bg-light-translucent-background ring-neutral-tones-text/35 dark:bg-dark-translucent-background absolute top-14 right-0 z-10 flex w-fit flex-col rounded-md p-2.5 ring md:w-full lg:top-18',
              )}
            >
              {options.map((option, index) => (
                <button
                  key={index}
                  className={clsx(
                    'font-inter-variable text-neutral-tones-text border-b-neutral-tones-text/35 hover:text-brand-byzantine global-transition cursor-pointer border-b py-2 text-left text-sm font-normal whitespace-nowrap first:pt-0 last:border-b-0 last:pb-0 disabled:cursor-not-allowed disabled:text-neutral-600',
                  )}
                  onClick={() => option.onClick()}
                >
                  {option.label}
                </button>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="global-scrollbar-style flex-1 overflow-auto px-0.5">
        <div className="flex w-full min-w-6xl flex-col">
          <div className="border-b-neutral-tones-text/50 relative mt-6 mb-12 flex flex-col gap-4 border-b pb-6 lg:flex-row lg:items-center lg:gap-9">
            <p className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg">
              <button
                onClick={() => {
                  if (
                    state?.search !== '' ||
                    state?.department !== '' ||
                    state?.status !== '' ||
                    state?.severity !== '' ||
                    state?.types !== '' ||
                    state?.limit !== 10
                  ) {
                    setState((prev) => ({
                      ...prev,
                      search: '',
                      department: '',
                      status: '',
                      severity: '',
                      types: '',
                      limit: 10,
                    }));
                  }
                }}
                className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex cursor-pointer items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg"
              >
                {state?.department !== '' ||
                state?.status !== '' ||
                state?.severity !== '' ||
                state?.types !== '' ||
                state?.search !== '' ||
                state?.limit !== 10 ? (
                  <FilterClearIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-9 lg:w-9" />
                ) : (
                  <FilterIcon className="h-6 w-6 lg:h-9 lg:w-9" />
                )}
                Filters
              </button>
            </p>
            <div className="flex flex-1 items-center gap-6">
              <Select
                filterSelect
                list={Object.values(IncidentsStatusEnum)
                  .filter((status) => status !== IncidentsStatusEnum.Draft)
                  .map((status) => ({
                    label: status,
                    value: status,
                  }))}
                value={state?.status}
                placeholder="Status"
                otherClasses={{
                  container: 'w-full max-w-34',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    status: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={departments}
                value={state?.department}
                placeholder="Departments"
                otherClasses={{ container: 'w-full max-w-174' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    department: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={incidentTypes}
                value={state?.types}
                placeholder="Incident Types"
                otherClasses={{ container: 'w-full max-w-48' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    types: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={Object.entries(IncidentsSeverityEnum).map(
                  ([, value]) => ({
                    label: value,
                    value,
                  }),
                )}
                value={state?.severity}
                placeholder="Severities"
                otherClasses={{ container: 'w-full max-w-32' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    severity: e,
                  }))
                }
                errorMessage=""
              />
            </div>
          </div>
        </div>
        {isIncidentsLoading ? (
          <div>
            <Loader
              variant="compact"
              otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
            />
          </div>
        ) : (
          <div className="w-full min-w-6xl">
            <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
              <h6
                className={clsx(
                  'text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 pr-6 font-bold lg:text-lg lg:leading-5',
                  data?.reOpen !== 0 ? 'text-right' : 'text-left',
                )}
              >
                Sr.
              </h6>
              <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Ref#
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-3 font-bold lg:text-lg lg:leading-5">
                Incident/Hazard
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Date of Report
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Type
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Severity
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Status
              </h6>
            </div>

            {data?.incidents?.length > 0 ? (
              <>
                {data?.incidents?.map((incident: Incidents, index: number) => {
                  return (
                    <Link
                      to={'/incident-review/' + incident.id}
                      key={index}
                      className="border-b-neutral-tones-text/50 grid grid-cols-12 items-center gap-4 border-b py-5"
                    >
                      <p
                        onClick={(e) => e.preventDefault()}
                        className={clsx(
                          'text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center pr-6 text-sm leading-4 font-bold lg:text-base lg:leading-5 lg:font-normal',
                          incident.reOpenCommentId
                            ? 'justify-between'
                            : data?.reOpen === 0
                              ? 'justify-start'
                              : 'justify-end',
                        )}
                      >
                        {incident.reOpenCommentId && (
                          <button className="group relative">
                            <WarningIcon className="h-5 min-h-5 w-5 min-w-5" />
                            <span className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-6 left-0 z-10 hidden min-w-36 items-center justify-center rounded px-2 py-1 text-xs font-normal shadow-lg group-hover:block group-focus:block">
                              Incident Reopened
                            </span>
                          </button>
                        )}
                        {(state.page - 1) * state.limit + index + 1}
                      </p>
                      <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal uppercase first-letter:uppercase lg:text-base lg:leading-5">
                        {incident.incidentRefId}
                      </p>
                      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-3 text-sm leading-4 font-normal lowercase first-letter:uppercase lg:text-base lg:leading-5">
                        {incident.title}
                      </p>
                      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
                        {moment(incident.reportedAt).format('YYYY-MM-DD')}
                      </p>
                      <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 text-sm leading-4 font-normal lg:text-base lg:leading-5">
                        {incident.incidentType?.name || ''}
                      </p>
                      <p
                        className={clsx(
                          'font-inter-variable lg:-tracking-form-input col-span-1 text-sm leading-4 font-bold lg:text-base lg:leading-5',
                          incident.severity === IncidentsSeverityEnum.Critical
                            ? 'text-brand-posh-pink'
                            : incident.severity === IncidentsSeverityEnum.Major
                              ? 'text-brand-fair-orange'
                              : incident.severity ===
                                  IncidentsSeverityEnum.Moderate
                                ? 'text-primary-yellow'
                                : 'text-brand-papery',
                        )}
                      >
                        {incident.severity}
                      </p>
                      <p
                        className={clsx(
                          'font-inter-variable lg:-tracking-form-input col-span-1 text-sm leading-4 font-bold whitespace-nowrap lg:text-base lg:leading-5',
                          incident.status === IncidentsStatusEnum?.Active
                            ? 'text-brand-posh-pink'
                            : incident.status === IncidentsStatusEnum?.Closed
                              ? 'text-brand-papery'
                              : 'text-brand-byzantine',
                        )}
                      >
                        {incident.status}
                      </p>
                    </Link>
                  );
                })}
              </>
            ) : (
              <NoItemsFound message={noDataMessage} />
            )}
          </div>
        )}
      </div>
      {data?.incidents?.length > 0 && (
        <Pagination
          currentPage={state.page}
          totalPages={data?.meta?.totalPages}
          totalItems={data?.meta?.total}
          limit={data?.meta?.limit}
          showingItems={data?.incidents?.length}
          onPageChange={handlePageChange}
          onLimitChange={(e) =>
            setState((prev) => ({ ...prev, limit: e, page: 1 }))
          }
          otherClasses=""
        />
      )}
    </div>
  );
};

export default IncidentReview;
